import { useEffect } from 'react';
import { wsEventBus } from 'src/core/realtime-connection';
import {
  UserPresence,
  UserPresenceStatus,
} from 'src/graphql/entities/user-presence/user-presence';
import { usePresenceStore } from 'src/store/user-presence-store';

const GRACE_SEC = 10;

const INACTIVITY_LIMIT_SEC = 20;

const isActiveNow = (expiresAt: number, status: UserPresenceStatus) =>
  status === UserPresenceStatus.active &&
  Math.floor(Date.now() / 1000) <= Number(expiresAt || 0) + GRACE_SEC;

let subStarted = false;
let subCleanup: (() => void) | null = null;

export const useUserPresenceSubscriptionToStore = () => {
  useEffect(() => {
    if (subStarted) return;
    subStarted = true;

    // @ts-expect-error this will be handled in a separate PR
    const sub = wsEventBus.onOperation('UserPresencePing').subscribe({
      // @ts-expect-error this will be handled in a separate PR
      next: (e: UserPresence & { type: 'UserPresencePing' }) => {
        const store = usePresenceStore.getState();
        const prev = store.byId[e.userId] as UserPresence | undefined;

        const nextStatus = isActiveNow(e.expiresAt, e.status)
          ? UserPresenceStatus.active
          : UserPresenceStatus.inactive;

        const nextTs = Number(e.ts ?? 0);
        const nextExpiresAt = Number(e.expiresAt ?? 0);

        // while active - ignore churn
        if (
          prev?.status === UserPresenceStatus.active &&
          nextStatus === UserPresenceStatus.active
        ) {
          return;
        }

        // while inactive - update only if lastSeen actually moved (e.g., ≥15s)
        if (
          prev?.status === UserPresenceStatus.inactive &&
          nextStatus === UserPresenceStatus.inactive &&
          nextTs - (prev.lastSeen || 0) < INACTIVITY_LIMIT_SEC
        ) {
          return;
        }

        store.upsertMany([
          {
            userId: e.userId,
            status: nextStatus,
            assigneeGroups: e.assigneeGroups ?? [],
            tz: e.tz ?? '',
            ts: nextTs,
            lastSeen:
              nextStatus === UserPresenceStatus.active ? (prev?.lastSeen ?? 0) : nextTs,
            expiresAt: nextExpiresAt,
            updatedAt: Math.floor(Date.now() / 1000),
          },
        ]);
      },
      error: console.error,
    });

    subCleanup = () => sub.unsubscribe();

    return () => {
      subCleanup?.();
      subCleanup = null;
      subStarted = false;
    };
  }, []);
};
